import { useCallback, useEffect, useMemo, useState } from 'react';
import { newId } from '@battleops/localstore';
import {
  deptGoalsView, goalHistory, cleanGoal, goalsFor, missingStarterDepartments, DEPT_STARTER_METRICS, DEPARTMENT_DEFAULTS,
  deptTotals, goalWindow, deskSignals, localToday, set, merge, commands, weekScores, allScores, listDepartments,
  homeProjects, homeProjectChoices, withLiveTime, bankable, localDateStr, pinHomeProject, unpinHomeProject, moveHomeProject,
  fieldDashboard, fieldJobCard, defaultFieldPins, mediaByTask, cleanHomeProjectIds, taskGoalLookup, taskGoalDepartment,
  homeCardRole, homePinsCopy, defaultHomePins, deptHasProjects, fieldHomeJobIds,
} from '@battleops/shared';
import { useTasks } from '../context/TaskContext';
import { useProjects } from '../context/ProjectContext';
import { useDepartments } from '../context/DepartmentContext';
import { useSession } from '../context/SessionContext';
import { useLocalCollection, useLocalDoc } from './useLocal';
import { useMetricCtx } from './useMetrics';
import { useCfo, usePayMap, useProjectProfit } from './useProfit';
import { useBooksFeed } from './useBooksFeed';
import { commitWrites } from './commit';

// Department goals and the score (docs/plans/scores.md S2) — the desk on Home and the department dashboard, phone
// and web. Everything is read from what's on the device; goals live on the department doc (`goals`).

/** What deptGoalsView reads, gathered once. */
export function useGoalData() {
  const { tasks: saved, timer, clockedIn } = useTasks();
  // the running timer counts live in every hours figure (Lj, Oct 11) — re-read once a minute while it runs
  const [tick, setTick] = useState(() => Date.now());
  const running = !!(timer?.taskId && timer?.startedAt && !timer?.pauseType);
  useEffect(() => {
    if (!running) return undefined;
    setTick(Date.now());
    const id = setInterval(() => setTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, [running, timer?.taskId, timer?.startedAt]);
  // exactly what clocking out would bank on the running task (bankable — on the clock, timer started this session)
  const tasks = useMemo(() => (timer?.taskId ? withLiveTime(saved, { taskId: timer.taskId, ms: bankable({ timer, clockedIn, taskId: timer.taskId, now: Math.max(Date.now(), tick) }).elapsed, date: localDateStr(Math.max(Date.now(), tick)) }) : saved), [saved, timer, clockedIn, tick]);
  const { projects } = useProjects();
  const { rawDepartments, departmentMode } = useDepartments();
  const metrics = useLocalCollection('metrics');
  const metricValues = useLocalCollection('metricValues');
  const media = useLocalCollection('media');   // the shared photo index: photos this week
  const personalMetrics = useLocalCollection('personalMetrics');
  const personalMetricValues = useLocalCollection('personalMetricValues');
  const receipts = useLocalCollection('fieldReceipts');
  const feed = useBooksFeed();                         // the books' Sales / Finance feed (S4): sales:* · finance:* goals
  const calls = useLocalCollection('calls');           // Sales' call goals (shared/callsmetrics): matched calls + the daily other count
  const customers = useLocalCollection('customers');   // who each call was with — customer or lead
  const metricCtx = useMetricCtx();
  const { view: cfo } = useCfo();
  const pay = usePayMap();                       // your BattleBooks rate — targets can follow it
  const { identity } = useSession() || {};
  const jobs = useLocalCollection('jobs');       // the books jobs — a job's contract value with its change orders
  const jobCost = useProjectProfit();            // Field's job margin = the job cost screen's own calculation
  const clockSessions = useLocalCollection('clockSessions');   // drives: the travel and fuel tiles
  const vehicle = useLocalDoc('meta', 'vehicle');              // L/100 km + $/L for the fuel estimate
  return useMemo(() => ({
    tasks, projects, departments: rawDepartments || [], departmentMode, metrics, metricValues, personalMetrics, personalMetricValues,
    receipts, metricCtx, cfo, sales: feed.sales, finance: feed.finance, pay, me: identity?.uid || null, jobs, jobCost, now: Math.max(Date.now(), tick),
    calls, customers, media, mediaByTask: mediaByTask(media), sessions: clockSessions, vehicle: vehicle || null,   // keyed once here; the job cards and the dashboard read it per task
  }), [tasks, tick, projects, rawDepartments, departmentMode, metrics, metricValues, personalMetrics, personalMetricValues, receipts, metricCtx, cfo, feed, pay, identity?.uid, jobs, jobCost, calls, customers, media, clockSessions, vehicle]);
}

/**
 * One department's goals + score, and what you can do with them.
 * → { view, history(goal, n), week, saveGoals(goals), saveGoal(goal), removeGoal(id), hours: { worked, planned }, signals }
 */
export function useDeptGoals(dept) {
  const data = useGoalData();
  const { saveDepartment } = useDepartments();
  const { clockedIn } = useTasks();
  const view = useMemo(() => (dept ? deptGoalsView(dept, data) : null), [dept, data]);
  const history = useCallback((goal, n = 6) => goalHistory(goal, dept, data, n), [dept, data]);
  const week = useMemo(() => (dept ? weekScores(dept, data) : []), [dept, data]);   // the line under the score

  // Starter goals become the department's own on the first edit (so the rest aren't lost).
  const current = useCallback(() => goalsFor(dept, data).filter((g) => !g.key.startsWith('personal:')).map(({ starter, ...g }) => g), [dept, data]);
  const saveGoals = useCallback((goals) => saveDepartment({ id: dept.id, goals: goals.map((g) => cleanGoal(g)).filter(Boolean) }), [dept, saveDepartment]);
  const saveGoal = useCallback((goal) => {
    const list = current();
    const i = list.findIndex((g) => g.id === goal.id);
    if (i >= 0) list[i] = { ...list[i], ...goal }; else list.push({ ...goal, id: goal.id || `g${newId()}` });
    return saveGoals(list);
  }, [current, saveGoals]);
  const removeGoal = useCallback((id) => saveGoals(current().filter((g) => g.id !== id)), [current, saveGoals]);

  // The footer: this week's hours here vs what's planned (estimates of this week's tasks here).
  const hours = useMemo(() => {
    if (!dept) return null;
    const w = goalWindow('week', data.now);
    const tot = deptTotals({ tasks: data.tasks, projects: data.projects, departments: data.departments, departmentId: dept.id, from: w.from, to: w.to, departmentMode: data.departmentMode });
    const plannedMin = data.tasks.filter((t) => !t.deletedAt && !t.parentId && t.scheduledDate >= w.from && t.scheduledDate <= w.to && (t.departmentId || null) === dept.id)
      .reduce((a, t) => a + (Number(t.estimatedMinutes) || 0), 0);
    return { workedMs: tot.ms, plannedMs: plannedMin * 60000 };
  }, [dept, data]);

  const signals = useMemo(() => (dept ? deskSignals({
    tasks: data.tasks, projects: data.projects, departments: data.departments, departmentId: dept.id, date: localToday(),
    clock: clockedIn ? { ...clockedIn, active: true } : null, receipts: data.receipts || [], departmentMode: data.departmentMode,
    fieldDepartment: view?.role === 'field' || view?.role === 'finance',
  }) : []), [dept, data, clockedIn, view?.role]);

  return { view, data, history, week, saveGoals, saveGoal, removeGoal, hours, signals };
}

/**
 * A task's goal (boards B7): the department it's kept on, its row in that department's view (open, or closed with its
 * final number), and `home` — the department a new goal on this task goes to (the task's own).
 * → { goal (view row) | null, dept, home, data, saveGoal, removeGoal }
 */
export function useTaskGoal(task) {
  const { rawDepartments } = useDepartments();
  const hit = useMemo(() => (task?.id ? taskGoalLookup(task.id, rawDepartments || []) : null), [task?.id, rawDepartments]);
  const data = useGoalData();
  const home = useMemo(() => (task ? taskGoalDepartment(task, data) : null), [task, data]);
  const dept = hit?.dept || home;
  const { view, saveGoal, removeGoal } = useDeptGoals(dept);
  const goal = useMemo(() => (hit && view ? [...view.goals, ...(view.closed || [])].find((g) => g.id === hit.goal.id) || null : null), [hit, view]);
  return { goal, dept, home, data, saveGoal, removeGoal };
}

/**
 * The projects pinned to a department's Home (Lj, Oct 11: "add app projects to the home screen … make them editable"),
 * each as its information card, and pin / unpin / move — saved on the department doc (`homeProjectIds`, in order).
 * Unpinning never touches the project. → { cards, choices, pin(id), unpin(id), move(id, dir), pinned }
 */
export function useHomeProjects(dept) {
  const data = useGoalData();
  const { activeTaskId } = useTasks();
  const role = homeCardRole(dept);
  const { ids, choices, pin, unpin, move } = useHomePins(dept, data);
  const cards = useMemo(() => (dept ? homeProjects({ ...dept, homeProjectIds: ids }, data, { activeTaskId }) : []), [dept, ids.join('|'), data, activeTaskId]);
  const copy = useMemo(() => homePinsCopy(dept), [dept?.id, dept?.name]);
  const hasProjects = useMemo(() => (dept ? deptHasProjects(dept, data) : false), [dept, data]);
  return { cards, choices, pin, unpin, move, pinned: cards.length > 0, role, copy, hasProjects };
}

// default pins are written once per department per session, however many cards on screen read the same pins
const SEEDED = new Set();

/**
 * The pin list behind every department Home's cards (`homeProjectIds`, in order). The first time a Field / non-Dev
 * Home opens with no pin ever set (the field missing, not an empty list the user left), its projects are pinned —
 * Field's live jobs (defaultFieldPins), everyone else's open projects newest activity first (defaultHomePins) — and
 * saved once, so Add / Remove / Edit work on a real list. Dev keeps its explicit pins. → { ids, choices, pin, unpin, move }
 */
function useHomePins(dept, data) {
  const { saveDepartment } = useDepartments();
  const role = homeCardRole(dept);
  const neverPinned = !!dept && role !== 'dev' && !Array.isArray(dept.homeProjectIds);
  const defaults = useMemo(() => (neverPinned ? (role === 'field' ? defaultFieldPins(dept, data) : defaultHomePins(dept, data)) : []), [neverPinned, role, dept, data]);
  useEffect(() => {
    if (!neverPinned || !defaults.length || SEEDED.has(dept.id)) return;
    SEEDED.add(dept.id);
    saveDepartment({ id: dept.id, homeProjectIds: defaults });
  }, [neverPinned, defaults.join('|')]);
  const ids = neverPinned ? defaults : cleanHomeProjectIds(dept?.homeProjectIds);
  const key = ids.join('|');
  const choices = useMemo(() => (dept ? homeProjectChoices({ ...dept, homeProjectIds: ids }, data) : { here: [], others: [] }), [dept, key, data]);
  const save = useCallback((next) => saveDepartment({ id: dept.id, homeProjectIds: next }), [dept, saveDepartment]);
  const pin = useCallback((id) => save(pinHomeProject(ids, id)), [key, save]);
  const unpin = useCallback((id) => save(unpinHomeProject(ids, id)), [key, save]);
  const move = useCallback((id, dir) => save(moveHomeProject(ids, id, dir)), [key, save]);
  return { ids, choices, pin, unpin, move };
}

/**
 * Field's Home (Lj, Oct 11: "the Field Home — same quality bar as the Dev Home"): the dashboard (shared fieldDashboard,
 * the running timer counted live) and the jobs pinned to Home as job cards. Pins share Dev's `homeProjectIds`; the first
 * time Field's Home opens with no pin ever set, its live jobs are pinned (defaultFieldPins) and saved — once: an empty
 * list the user left stays empty. → { x, jobs: { cards, choices, pin, unpin, move } }
 */
export function useFieldDashboard(dept, { range = 'week', custom = null } = {}) {
  const data = useGoalData();
  const { clockedIn, activeTaskId } = useTasks();
  const x = useMemo(() => (dept ? fieldDashboard(dept, data, { clock: clockedIn, activeTaskId, range, custom }) : null), [dept, data, clockedIn, activeTaskId, range, custom?.from, custom?.to]);
  const { ids, choices, pin, unpin, move } = useHomePins(dept, data);   // Field's live jobs pinned the first time (defaultFieldPins)
  const cards = useMemo(() => {
    const byId = new Map((data.projects || []).map((p) => [p.id, p]));
    return fieldHomeJobIds(dept, data, ids).map((id) => byId.get(id)).filter((p) => p && !p.deletedAt).map((p) => ({ ...fieldJobCard(p, data, { activeTaskId }), pinned: ids.includes(p.id) }));
  }, [dept, ids.join('|'), data, activeTaskId]);
  const copy = useMemo(() => homePinsCopy(dept), [dept?.id, dept?.name]);
  const hasProjects = useMemo(() => (dept ? deptHasProjects(dept, data) : false), [dept, data]);
  return { x, data, jobs: { cards, choices, pin, unpin, move, copy, hasProjects } };
}

/** "All": every department's score and the business average (shared allScores). */
export function useAllScores() {
  const data = useGoalData();
  return useMemo(() => allScores(listDepartments(data.departments || []), data), [data]);
}

/**
 * Add the departments Lj picked on Oct 10 (Sales, Marketing, Finance, Dev) and the counters their starter goals use
 * (outreach touches, email sign-ups) — one group. → { add, missing }
 */
export function useStarterDepartments() {
  const { rawDepartments } = useDepartments();
  const metrics = useLocalCollection('metrics');
  const { identity } = useSession() || {};
  const missing = useMemo(() => missingStarterDepartments(rawDepartments || []), [rawDepartments]);
  const add = useCallback(async () => {
    const at = Date.now();
    const writes = [];
    missing.forEach((d, i) => {
      const id = newId();
      writes.push(set(`departments/${id}`, { ...DEPARTMENT_DEFAULTS, ...d, id, order: at + i, createdAt: at, updatedAt: at }));
    });
    const ctx = { newId, now: at, uid: identity?.uid || null, metrics };
    for (const m of DEPT_STARTER_METRICS) {
      if (metrics.some((x) => x.id === m.id)) continue;
      writes.push(...commands.saveMetric(ctx, { metric: { ...m } }).writes);
    }
    if (writes.length) await commitWrites(writes);
    return missing.length;
  }, [missing, metrics, identity?.uid]);
  return { missing, add };
}

/**
 * General's "find it a home": move a project to another department — the project's home and, in department mode,
 * its open tasks that sat in the old one — in one group.
 */
export function useMoveHome() {
  const { tasks } = useTasks();
  return useCallback(async (project, toId, fromId = null) => {
    const at = Date.now();
    const writes = [merge(`projects/${project.id}`, { departmentId: toId, updatedAt: at })];
    for (const t of tasks) {
      if (t.projectId !== project.id || t.status === 'done' || t.deletedAt) continue;
      const here = t.departmentId ? t.departmentId === fromId : (fromId === 'general' || !fromId);
      if (!here) continue;
      writes.push(merge(`tasks/${t.id}`, { departmentId: toId, updatedAt: at }));
    }
    await commitWrites(writes);
    return writes.length - 1;
  }, [tasks]);
}
